import React, { useCallback, useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// One story a week is the plan for everyone. A paid member can add a second story to THIS week only; next week goes back to
// one. A free account sees the option, locked, with a short note. Only learner accounts see it.
export default function StoriesPerWeekChoice({ onChanged }) {
  const { user, setUser } = useAuth();
  const [thisWeek, setThisWeek] = useState(null);   // 1 or 2 stories this week (from /today), null until known
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isLearner = !!user && user.role === "student";

  const load = useCallback(() => {
    client.get("/today")
      .then(({ data }) => setThisWeek(data?.enrolled ? (data.position?.story_b_seq ? 2 : 1) : null))
      .catch(() => setThisWeek(null));
  }, []);
  useEffect(() => { if (isLearner) load(); }, [isLearner, load]);

  if (!isLearner || thisWeek === null) return null;
  const paid = !!user.membership_paid;

  const choose = async (n) => {
    if (n === thisWeek || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await client.put("/me/stories-per-week", { stories_per_week: n });
      setUser(data.user);
      setThisWeek(data.this_week);
      if (onChanged) onChanged();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const option = (n, label, locked) => {
    const active = thisWeek === n;
    return (
      <button
        type="button"
        onClick={() => choose(n)}
        disabled={busy || locked}
        aria-pressed={active}
        data-testid={`stories-per-week-${n}`}
        className={`rounded-full px-4 py-2 text-sm font-bold min-h-[44px] transition ${
          active ? "bg-hv-navy text-white" : "bg-white text-hv-navy border-2 border-[#E5DFC8]"
        } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {label}
      </button>
    );
  };

  return (
    <section className="rounded-[2rem] bg-white shadow-sm p-5 sm:p-6" data-testid="stories-per-week">
      <div className="text-sm font-bold text-hv-navy">Stories this week</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {option(1, "1 story", false)}
        {option(2, "2 stories", !paid)}
      </div>
      <p className="mt-3 text-sm text-hv-slate" data-testid="stories-per-week-note">
        {!paid
          ? "One new story every week. The paid plan lets you add a second story to any week."
          : thisWeek === 2
            ? "Two stories this week. Next week goes back to one, and you can add a second again any time."
            : "One story this week. Choose 2 stories to add a second one to this week only."}
      </p>
      {err && <p className="mt-2 text-sm font-bold text-hv-coral" role="alert">{err}</p>}
    </section>
  );
}
